import { useLocation, useNavigate } from 'react-router-dom'
import { Menu, Zap, HelpCircle } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { useQuery } from '@tanstack/react-query'
import { billingApi } from '@/services/billingApi'
import { useTours } from '@/hooks/useTours'
import { getTourForPath } from '@/services/tours/tourCatalog'

interface HeaderProps {
  onMenuClick: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { startTourForPath } = useTours()
  // Solo se ofrece la guía en las secciones que realmente tienen una guía
  // contextual; en el resto el botón sería un botón muerto.
  const hasTourForSection = Boolean(getTourForPath(location.pathname))

  const { data: billing } = useQuery({
    queryKey: ['billing-summary-header'],
    queryFn: () => billingApi.getBillingSummary(),
    enabled: !!user,
    staleTime: 30000,
  })

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <header className="bg-white shadow-sm sticky top-0 z-10 flex-shrink-0 lg:h-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-0 lg:h-full flex justify-between items-center">
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <Menu className="h-6 w-6" />
          </button>
          <img
            src="/new/IridiaChat_Logo_Fondo_Claro_Transparente.png"
            alt="IridiaChat"
            className="h-8 w-auto object-contain"
          />
        </div>
        <div className="flex items-center space-x-3 sm:space-x-4">
          {billing && (
            <button
              onClick={() => navigate('/app/billing')}
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full transition-colors border ${
                billing.status === 'blocked'
                  ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                  : billing.credits.percentageUsed >= 80
                  ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
              }`}
              title="Ver plan y balance de créditos"
            >
              <Zap className="w-3.5 h-3.5" />
              <span>
                {billing.credits.remaining.toLocaleString()} créditos
                {billing.isTrial && billing.daysRemaining !== null ? ` · ${billing.daysRemaining}d trial` : ''}
              </span>
            </button>
          )}
          <span className="text-sm text-gray-600 hidden md:inline">{user?.email || ''}</span>
          {hasTourForSection && (
            <button
              type="button"
              data-tour="header-help"
              onClick={() => startTourForPath(location.pathname)}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075f55]"
              title="Abrir la guía interactiva de esta sección"
              aria-label="Abrir la guía interactiva de esta sección"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="hidden sm:inline">Guía</span>
            </button>
          )}
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </header>
  )
}
